import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const TOKEN_KEY = "pixelempire_access_token";

/**
 * Auth persistence — belt-and-suspenders.
 *
 * Backend sets an httpOnly `access_token` cookie on register/login. Same-origin
 * requests carry it automatically. HOWEVER, some browsers (Safari private mode,
 * strict-tracking Chrome, in-app webviews) drop third-party or Secure-less
 * cookies silently — the browser network tab won't even show them.
 *
 * As a defensive layer we ALSO store the JWT (returned in the same response
 * body as `token`) in localStorage and forward it via the `Authorization:
 * Bearer …` header the backend already understands. So auth survives even
 * when the cookie gets nuked by browser policy.
 */
export const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private-mode / storage disabled */
  }
};

export const clearStoredToken = () => setStoredToken(null);

export const http = axios.create({ baseURL: API, withCredentials: true });

// Attach the localStorage-stored JWT to every outbound request as a fallback
// auth mechanism. If the cookie is also present the backend prefers it, but
// this ensures we never lose the session just because Safari purged our cookie.
http.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Auto-persist whenever the backend hands us a fresh token in a response body.
http.interceptors.response.use(
  (response) => {
    const token = response?.data?.token;
    if (typeof token === "string" && token.length > 20) {
      setStoredToken(token);
    }
    return response;
  },
  (error) => Promise.reject(error),
);

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const currency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export const compact = (n) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
